/**
 * Pledge: promise now, pay later (GH-410, SPEC.md §13.3).
 *
 * The screen a donor reaches when they intend to give but have no card on them — common at an
 * event, and the reason this exists at all.
 *
 * The design problem is that a pledge must never read as a payment. Three things enforce that
 * here: the amount is labelled "pledge" everywhere it appears, no payment field is ever shown, and
 * the confirmation sentence comes from the server rather than from copy in this bundle, so
 * "nothing has been charged" cannot be edited away.
 *
 * A proven contact is required: it is what ties the promise to a person who can come back and pay
 * it. The donor chooses a mobile number or an email address (TestFlight feedback, 2026-09-15:
 * *"some people want email, it can be verified that way"*). Either is proven by a code sent on
 * that channel, and the proof is consumed by the server in the same transaction that records the
 * promise — this component never claims a verification, it only carries the code.
 *
 * ## No reminder is promised (GH-809)
 *
 * This screen used to say "{organization} will remind you". Nothing sends a pledge reminder — no
 * job, no message template, no consent purpose wired to one — so the sentence was a promise made
 * on the organization's behalf that nobody keeps. It now says what is true: nothing is charged,
 * and the promise can be paid later by signing in (the web's "Pay pledge", `Home.tsx`). If
 * reminders are built, the sentence comes back with them, not before.
 *
 * ## The channel pair
 *
 * Two native radios drawn as the designation chips are, rather than a pair of `aria-pressed`
 * buttons: one question, one answer, one tab stop with arrow keys between the options, and a
 * `checked` state that survives forced-colours mode — `DesignationPicker` states the reasoning at
 * length and this borrows it whole. Phone stays the default and its path is unchanged; email is
 * the one verification a guest can complete on a stack where messaging is suppressed and mail is
 * not.
 */
import { formatMoney, type MoneyWire } from "@givehere/contracts";
import { useCallback, useState } from "react";
import {
  ApiError,
  SYNTHETIC_PAYMENTS,
  createPledge,
  startEmailVerification,
  startPhoneVerification,
  type PledgeContact,
} from "./api.js";
import { Explain } from "./Explain.js";

type Stage = "contact" | "code" | "done";
type Channel = "phone" | "email";

const CHANNELS: ReadonlyArray<{ id: Channel; label: string }> = [
  { id: "phone", label: "Text me" },
  { id: "email", label: "Email me" },
];

/**
 * Enough to enable the button and no more: something either side of an `@`, and a dot after it.
 * The server validates the address; this only keeps "Send code" from firing on half of one.
 */
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export function PledgeFlow({
  amount,
  quoteToken,
  organizationDisplayName,
  /** Pre-filled when a wallet supplied one. Convenience only — it is still verified by SMS. */
  suggestedPhone,
  suggestedName,
  onCancel,
  onRecorded,
}: {
  amount: MoneyWire;
  quoteToken: string;
  organizationDisplayName: string;
  suggestedPhone?: string | undefined;
  suggestedName?: string | undefined;
  onCancel: () => void;
  /**
   * The promise is recorded — the server said so (GH-937). A notification, never a navigation:
   * this screen stays on its own confirmation either way; the caller may remember the fact.
   */
  onRecorded?: (() => void) | undefined;
}) {
  const [stage, setStage] = useState<Stage>("contact");
  const [channel, setChannel] = useState<Channel>("phone");
  const [phone, setPhone] = useState(suggestedPhone ?? "");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [last4, setLast4] = useState("");
  const [developmentCode, setDevelopmentCode] = useState<string | null>(null);
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const address = email.trim();
  /** Exactly one of the two goes on the wire — the contract refuses both and refuses neither. */
  const contact: PledgeContact = channel === "phone" ? { phone } : { email: address };
  const canSend = channel === "phone" ? phone.trim().length >= 7 : looksLikeEmail(email);
  /** What the code went to, in the donor's own words: a masked number, or the address they typed. */
  const destination = channel === "phone" ? `••••${last4}` : address;

  const send = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      if (channel === "phone") {
        const started = await startPhoneVerification(phone);
        setVerificationId(started.verificationId);
        setLast4(started.last4);
        setDevelopmentCode(started.developmentCode);
      } else {
        const started = await startEmailVerification(address);
        setVerificationId(started.verificationId);
        setDevelopmentCode(started.developmentCode);
      }
      setStage("code");
    } catch (caught: unknown) {
      setError(caught instanceof ApiError ? caught.message : "Could not send a code.");
    } finally {
      setBusy(false);
    }
  }, [channel, phone, address]);

  const confirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const pledge = await createPledge({
        quoteToken,
        displayedAmount: amount,
        ...contact,
        verificationId,
        code,
        ...(suggestedName?.trim() ? { displayName: suggestedName.trim() } : {}),
      });
      setSummary(pledge.summary);
      setStage("done");
      onRecorded?.();
    } catch (caught: unknown) {
      setError(caught instanceof ApiError ? caught.message : "Could not record that pledge.");
    } finally {
      setBusy(false);
    }
  }, [quoteToken, amount, contact, verificationId, code, suggestedName, onRecorded]);

  if (stage === "done") {
    return (
      <main className="card" role="status">
        <p className="badge badge--pledge">Pledged</p>
        <h1 className="screen-title">Thank you</h1>
        {/* Server-composed, so the "nothing has been charged" guarantee travels with the fact. */}
        <p className="pledge__summary">{summary}</p>
        {/* What is true, and nothing more: a reminder is not sent by anything (GH-809). A pledge
            proven by phone is paid later by signing in with that number; sign-in is by phone
            only, so an email pledge is told only where it was confirmed. */}
        <p className="muted small">
          {channel === "phone"
            ? `To pay it later, sign in with the number ending ${last4}.`
            : `Confirmed at ${address}.`}
        </p>
      </main>
    );
  }

  return (
    <main className="card">
      <button type="button" className="back" onClick={onCancel}>
        &#x2190; Back
      </button>

      <h1 className="screen-title">Pledge {formatMoney(amount)}</h1>
      <p className="muted small pledge__lede">Nothing is charged now. You can pay it later.</p>

      {stage === "contact" && (
        <>
          <fieldset className="block pledge__channels">
            {/* The group's name is for the screen reader, which hears the two options without
                the code field in view. */}
            <legend className="visually-hidden">Where to send your code</legend>
            <div className="designations__options">
              {CHANNELS.map((option) => {
                const selected = option.id === channel;
                const inputId = `pledge-channel-${option.id}`;
                return (
                  <div
                    key={option.id}
                    className={selected ? "designation designation--selected" : "designation"}
                  >
                    <input
                      type="radio"
                      className="visually-hidden"
                      id={inputId}
                      name="pledge-channel"
                      value={option.id}
                      checked={selected}
                      onChange={() => {
                        setChannel(option.id);
                        setError(null);
                      }}
                    />
                    {/* The label fills the chip, so the whole chip is the target. */}
                    <label className="designation__body" htmlFor={inputId}>
                      <span className="designation__name">{option.label}</span>
                    </label>
                  </div>
                );
              })}
            </div>
          </fieldset>

          {channel === "phone" ? (
            <label className="field">
              <span>Mobile number</span>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="+1 555 010 0100"
                value={phone}
                onChange={(changed) => setPhone(changed.target.value)}
              />
            </label>
          ) : (
            <label className="field">
              <span>Email address</span>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.org"
                value={email}
                onChange={(changed) => setEmail(changed.target.value)}
              />
            </label>
          )}
          <Explain
            className="muted small"
            label="Only to confirm it's you"
            title="What this is used for"
          >
            <p>
              Used to confirm this pledge to {organizationDisplayName} is yours, so you can pay it
              later. Nothing else.
            </p>
          </Explain>

          {error && (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            className="pay"
            disabled={busy || !canSend}
            onClick={() => void send()}
          >
            {busy ? "Sending…" : "Send code"}
          </button>
        </>
      )}

      {stage === "code" && (
        <>
          <label className="field">
            <span>6-digit code sent to {destination}</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="field__code"
              value={code}
              onChange={(changed) => setCode(changed.target.value.replace(/\D/g, ""))}
            />
          </label>

          {/* Present only where nothing can actually send a message and the environment is not
              production — the API decides that, not this bundle. */}
          {developmentCode && (
            <p className="notice" role="note">
              {SYNTHETIC_PAYMENTS ? "Test mode" : "Messaging is off"} — your code is{" "}
              <strong>{developmentCode}</strong>
            </p>
          )}

          {error && (
            <p className="notice notice--error" role="alert">
              {error}
            </p>
          )}

          <button
            type="button"
            className="pay"
            disabled={busy || code.length !== 6}
            onClick={() => void confirm()}
          >
            {busy ? "Recording…" : `Pledge ${formatMoney(amount)}`}
          </button>

          <button type="button" className="link link--block" onClick={() => setStage("contact")}>
            Use a different {channel === "phone" ? "number" : "address"}
          </button>
        </>
      )}
    </main>
  );
}
